import { useState, useEffect } from 'react';
import { Shield, CheckCircle, FileText, Lock, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';

const TERMS_ACCEPTED_KEY = 'tempvault_terms_accepted';

export const TermsModal = () => {
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    const hasAccepted = localStorage.getItem(TERMS_ACCEPTED_KEY);
    if (!hasAccepted) {
      setOpen(true);
    }
  }, []);

  const handleAccept = () => {
    if (accepted) {
      localStorage.setItem(TERMS_ACCEPTED_KEY, 'true');
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg glass-premium border-2 border-primary/30 glow-border" hideCloseButton>
        <DialogHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="absolute -inset-3 gradient-bg rounded-full opacity-50 blur-xl animate-pulse-slow" />
              <div className="relative w-16 h-16 rounded-2xl gradient-bg flex items-center justify-center shadow-glow-intense animate-float">
                <Shield className="w-8 h-8 text-primary-foreground" />
              </div>
            </div>
          </div>
          <DialogTitle className="text-2xl font-bold gradient-text">
            Welcome to TempVault
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Please review and accept our terms to continue
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-64 pr-4 my-4">
          <div className="space-y-4 text-sm text-muted-foreground">
            <div className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50">
              <FileText className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">Terms of Service</h4>
                <p>By using TempVault, you agree to use our temporary email service responsibly. This service is intended for legitimate purposes such as protecting your privacy when signing up for services.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50">
              <Lock className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">Privacy Policy</h4>
                <p>Your inbox is secured with a unique token stored only in your browser. Emails are automatically deleted after 24 hours. We do not share your data with third parties.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50">
              <Sparkles className="w-5 h-5 text-accent mt-0.5 shrink-0" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">Acceptable Use</h4>
                <p>Do not use TempVault for illegal activities, spam, harassment, or fraud. We reserve the right to terminate access for violations of these terms.</p>
              </div>
            </div>

            <div className="text-xs text-muted-foreground/70 mt-4">
              <p>This site may display advertisements from third-party networks. By continuing, you acknowledge that you may see ads while using our service.</p>
            </div>
          </div>
        </ScrollArea>

        <div className="space-y-4">
          <div className="flex items-center space-x-3 p-3 rounded-xl bg-secondary/30 border border-border/50">
            <Checkbox
              id="terms"
              checked={accepted}
              onCheckedChange={(checked) => setAccepted(checked === true)}
              className="border-primary data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
            <label
              htmlFor="terms"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              I accept the Terms of Service and Privacy Policy
            </label>
          </div>

          <Button
            onClick={handleAccept}
            disabled={!accepted}
            className="w-full h-12 gradient-bg text-primary-foreground shadow-glow hover:shadow-glow-intense transition-all font-bold text-base disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            Continue to TempVault
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
